import { useEffect, useRef, useCallback, useState } from 'react';
import {
  isCompletedStatus,
  isFailedStatus,
  isRunningStatus,
  normalizeStatus
} from '@vehix/portal/utils/executionStatus';
import { UnifiedExecutionHistory } from '@vehix/portal/types/executionHistory';
import { useToast } from '@vehix/shared/admin/portal-bridge';
import { dataCollectionAPI } from '@vehix/portal/services/api/dataCollection';

interface ApifyStatusPollingOptions {
  enabled?: boolean;
  pollingInterval?: number; // milliseconds
  onStatusUpdate?: (runId: string, newStatus: string) => void;
}

/**
 * Hook to poll Apify for run status updates for long-running tasks
 * Only polls for runs that are in 'running' status and are data collection tasks
 */
export function useApifyStatusPolling(
  executions: UnifiedExecutionHistory[],
  options: ApifyStatusPollingOptions = {}
) {
  const {
    enabled = true,
    pollingInterval = 30000, // 30 seconds default
    onStatusUpdate
  } = options;

  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const { toast } = useToast();
  const [pollingStatus, setPollingStatus] = useState<'idle' | 'polling' | 'error'>('idle');
  const lastPollRef = useRef<Date>();

  const pollApifyStatuses = useCallback(async () => {
    // Filter for running data collection executions with run_ids
    const runningExecutions = executions.filter(
      exec =>
        exec.executionType === 'data_collection' &&
        isRunningStatus(normalizeStatus(exec.status)) &&
        exec.run_id
    );

    if (runningExecutions.length === 0) {
      return;
    }

    setPollingStatus('polling');
    lastPollRef.current = new Date();

    try {
      // Refresh statuses from backend (authoritative and auto-syncing Apify when stale)
      const runIds = runningExecutions.map(exec => exec.run_id).filter((id): id is string => !!id);
      const refreshedExecutions = await Promise.all(
        runIds.map(async runId => ({
          runId,
          execution: await dataCollectionAPI.getExecution(runId)
        }))
      );

      for (const { runId, execution } of refreshedExecutions) {
        if (!execution) continue;
        const normalizedStatus = normalizeStatus(execution.status);
        if (isRunningStatus(normalizedStatus)) {
          continue;
        }

        // If completed successfully, trigger backfill to process dataset
        if (isCompletedStatus(normalizedStatus)) {
          toast({
            title: 'Processing Dataset',
            description: `Processing data for "${execution.task_name || 'Unknown'}"...`
          });

          try {
            const backfillResult = await dataCollectionAPI.backfillRun(runId, true);

            toast({
              title: 'Task Completed',
              description: `"${execution.task_name || 'Unknown'}" completed with ${backfillResult.result.records || 0} records`,
              variant: 'default'
            });
          } catch (backfillError) {
            console.error(`Backfill failed for run ${runId}:`, backfillError);
            toast({
              title: 'Dataset Processing Failed',
              description: `"${execution.task_name || 'Unknown'}" status updated but dataset processing failed`,
              variant: 'destructive'
            });
          }
        } else if (isFailedStatus(normalizedStatus)) {
          toast({
            title: 'Task Failed',
            description: `"${execution.task_name || 'Unknown'}" failed`,
            variant: 'destructive'
          });
        }

        if (onStatusUpdate) {
          onStatusUpdate(runId, normalizedStatus);
        }
      }

      setPollingStatus('idle');
    } catch (error) {
      console.error('Error polling Apify statuses:', error);
      setPollingStatus('error');
    }
  }, [executions, toast, onStatusUpdate]);

  // Set up polling interval
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Initial poll
    pollApifyStatuses();

    // Set up interval
    intervalRef.current = setInterval(pollApifyStatuses, pollingInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, pollingInterval, pollApifyStatuses]);

  return {
    pollingStatus,
    lastPoll: lastPollRef.current,
    pollNow: pollApifyStatuses
  };
}
